import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { useBidContext } from '../../context/BidContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import AgreementButton from '../../components/AgreementButton';
import PrimaryButton from '../../components/PrimaryButton';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 24px;
`;

// 정보 섹션
const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 8px;
  border-top: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #f0f0f0;
`;

const LotInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LotName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  line-height: 1.4;
  text-align: left;
  align-self: flex-start;
`;

const LotId = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: left;
  align-self: flex-start;
`;

// 금액 섹션
const AmountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding-top: 20px;
  margin-bottom: 8px;
`;

const FeeCalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const FeeDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e9ecef;
  margin: 12px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #7a6647;
  padding-top: 8px;
`;

// 툴팁 관련 스타일
const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const QuestionIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #666666;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #333333;
  }
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s, visibility 0.2s;
  margin-bottom: 4px;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333333;
  }
`;

// 결제 수단 섹션
const PaymentMethodSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 20px 20px 0;
  margin-bottom: 8px;
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RadioOption = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 16px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: white;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const RadioCircle = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.selected ? '#69b5b0' : '#c0c0c0'};
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #69b5b0;
    opacity: ${props => props.selected ? 1 : 0};
    transition: opacity 0.2s;
  }
`;

const RadioLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #262626;
`;

// 결제 유의사항 스타일
const PaymentNoticeContainer = styled.div`
  background: #f8f5f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 137px; /* 최소 높이로 변경 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const PaymentNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const InfoIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: #666666;
  flex-shrink: 0;
`;

const PaymentNoticeList = styled.ul`
  margin: 0;
  padding: 0 0 0 12px; /* 왼쪽에 12px 여백 추가 */
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PaymentNoticeItem = styled.li`
  font-size: 14px;
  color: #666666;
  line-height: 1.4;
  position: relative;
  padding-left: 16px; /* 점과 텍스트 사이 간격 줄임 */
  text-align: left; /* 명시적으로 왼쪽 정렬 */
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #999999;
  }
`;

// 동의 섹션 스타일
const AgreementSectionWrapper = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
`;

// 고정 버튼 컨테이너
const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 75px; /* 앱에서 하단 네비게이션 위로 */
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 999;
  border-top: 1px solid #e9ecef;

  @media (min-width: 768px) {
    bottom: 0; /* 웹에서는 항상 바닥에 고정 */
    padding: 20px 24px;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  

  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

const paymentTexts = {
  ko: {
    title: '결제',
    infoSection: '경매 정보',
    amountSection: '결제 금액',
    winningBid: '낙찰가',
    auctionHouseFee: '경매장 수수료',
    onlineAuctionFee: '온라인경매 수수료',
    feeVAT: '수수료 부가세',
    feeVATTooltip: '경매장 수수료와 온라인경매 수수료의 합산에 대한 부가세입니다',
    totalAmount: '총 합계',
    currency: 'KRW',
    paymentMethodSection: '결제 수단',
    bidspacePay: 'BidSpace 페이',
    bankTransfer: '계좌이체',
    agreementSection: '동의사항',
    allAgreeTitle: '전체 동의',
    paymentTermsTitle: '위 유의사항을 확인했습니다',
    paymentTermsDetailTitle: '결제 이용약관 상세',
    agreeButton: '동의',
    paymentNoticeTitle: '결제 시 유의사항',
    paymentNoticeItem1: '본 결제에는 다음 항목이 포함되어 있습니다',
    paymentNoticeItem2: '- 낙찰가',
    paymentNoticeItem3: '- 경매 수수료 (경매장 별로 상이, 예 : 18~25%)',
    paymentNoticeItem4: '- 온라인 경매 수수료 (경매장 설정에 따라 별도 부과)',
    paymentNoticeItem5: '- 위 수수료에 대한 부가가치세 10%',
    paymentNoticeItem6: '최종 결제 금액은 낙찰가와 수수료, 세금을 포함한 총합입니다.',
    paymentNoticeItem7: '본 결제에는 관세가 포함되어 있지 않으며, 낙찰 물품 수입 시 별도로 납부하셔야 합니다.'
  },
  en: {
    title: 'Payment',
    infoSection: 'Auction Information',
    amountSection: 'Payment Amount',
    winningBid: 'Winning Bid',
    auctionHouseFee: 'Auction House Fee',
    onlineAuctionFee: 'Online Auction Fee',
    feeVAT: 'Fee VAT',
    feeVATTooltip: 'VAT on the combined auction house and online auction fees',
    totalAmount: 'Total Amount',
    currency: 'USD',
    paymentMethodSection: 'Payment Method',
    bidspacePay: 'BidSpace Pay',
    bankTransfer: 'Bank Transfer',
    agreementSection: 'Agreements',
    allAgreeTitle: 'Agree All',
    paymentTermsTitle: 'I have confirmed the above precautions',
    paymentTermsDetailTitle: 'Payment Terms Details',
    agreeButton: 'Agree',
    paymentNoticeTitle: 'Payment Precautions',
    paymentNoticeItem1: 'This payment includes the following items:',
    paymentNoticeItem2: '- Winning bid amount',
    paymentNoticeItem3: '- Auction house fee (varies by auction house, e.g., 18~25%)',
    paymentNoticeItem4: '- Online auction fee (separately charged according to auction house settings)',
    paymentNoticeItem5: '- 10% value-added tax on the above fees',
    paymentNoticeItem6: 'The final payment amount is the total including the winning bid, fees, and taxes.',
    paymentNoticeItem7: 'This payment does not include customs duties, which must be paid separately when importing the winning item.'
  }
};

function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const { currentLotData } = useBidContext(); // Context에서 데이터 가져오기
  const texts = paymentTexts[lang];
  
  // location.state에서 낙찰 내역 데이터 가져오기
  const { auctionData, fromWonAuctions } = location.state || {};
  
  // 실제 사용할 데이터 결정 (낙찰 내역에서 온 경우 auctionData, 아니면 currentLotData)
  const lotData = fromWonAuctions ? auctionData : currentLotData;
  
  // API에서 가져온 lot 정보가 없으면 이전 페이지로 돌아가기
  useEffect(() => {
    if (!lotData) {
      toast.error('LOT 정보를 찾을 수 없습니다.');
      navigate(-1);
      return;
    }
  }, [lotData, navigate]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('bidspace-pay');
  const [isIndividualAgreed, setIsIndividualAgreed] = useState(false);
  const [showVATTooltip, setShowVATTooltip] = useState(false);

  // 가정: 낙찰가는 lotData에서 가져옴
  // 낙찰 내역에서 온 경우 totalAmount를 사용, 아니면 startPrice 사용
  const winningBidPrice = fromWonAuctions ? 
    (auctionData?.totalAmount || auctionData?.startPrice || 1000000) : 
    (currentLotData?.startPrice || 1000000);

  // 툴팁 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVATTooltip) {
        setShowVATTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showVATTooltip]);

  // API에서 받았다고 가정하는 수수료율 - 실제 API 데이터 사용
  const feeRates = {
    auctionHouseFeeRate: fromWonAuctions ? 
      (auctionData?.auctionFeeRate || 20) : 
      (currentLotData?.auction?.auctionFeeRate || 20),
    onlineAuctionFeeRate: fromWonAuctions ? 
      (auctionData?.platformFeeRate || 5) : 
      (currentLotData?.auction?.platformFeeRate || 5),
    vatRate: 10 // 10% (고정)
  };

  // 수수료 계산 함수
  const calculateFees = (bidPrice: number) => {
    const auctionHouseFee = bidPrice * (feeRates.auctionHouseFeeRate / 100);
    const onlineAuctionFee = bidPrice * (feeRates.onlineAuctionFeeRate / 100);
    const totalFees = auctionHouseFee + onlineAuctionFee;
    const feeVAT = totalFees * (feeRates.vatRate / 100);
    const totalAmount = bidPrice + totalFees + feeVAT;

    return {
      auctionHouseFee: Math.floor(auctionHouseFee),
      onlineAuctionFee: Math.floor(onlineAuctionFee),
      feeVAT: Math.floor(feeVAT),
      totalAmount: Math.floor(totalAmount)
    };
  };

  const formatNumber = (value: number) => {
    if (lang === 'ko') {
      return value.toLocaleString();
    } else {
      // USD 변환
      const exchangeRate = fromWonAuctions ? 
        (auctionData?.exchangeRate || 1409.09) : 
        (currentLotData?.exchangeRate || 1409.09);
      const usdValue = Math.round((value / exchangeRate) * 100) / 100;
      return usdValue.toFixed(2);
    }
  };

  // 이미지 URL 처리 함수
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  const handlePaymentMethodSelect = (method: string) => {
    setSelectedPaymentMethod(method);
  };

  const isFormValid = () => {
    return selectedPaymentMethod && isIndividualAgreed;
  };

  const handlePaymentSubmit = async () => {
    if (!lotData?.index || !selectedPaymentMethod) {
      toast.error('결제 정보가 올바르지 않습니다.');
      return;
    }

    try {
      // 결제 API 호출 (실제 API에 맞게 수정 필요)
      // const response = await processPayment(lotData.index, {
      //   paymentMethod: selectedPaymentMethod,
      //   amount: calculateFees(winningBidPrice).totalAmount
      // });

      // 임시로 성공 처리
      toast.success('결제가 완료되었습니다!');
      
      // 성공 후 이전 페이지로 돌아가기
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error: any) {
      devError('결제 실패:', error);
      
      // 에러 응답에서 메시지 추출
      let errorMessage = '결제에 실패했습니다.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDisabled = () => {
    // 비활성화 상태일 때의 처리
    toast.warning('결제 수단을 선택하고 동의해주세요');
  };

  const fees = calculateFees(winningBidPrice);

  const Layout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 정보 섹션 */}
            <InfoSection>
              <LotImage 
                src={lotData?.images?.[0] ? getImageSrc(lotData.images[0]) : "/common/no_image.svg"} 
                alt="Lot Image"
                onError={(e) => {
                  e.currentTarget.src = '/common/no_image.svg';
                }}
              />
              <LotInfoContainer>
                <LotName>{lotData?.name || 'LOT 정보를 불러올 수 없습니다.'}</LotName>
                <LotId>LOT #{lotData?.index || '---'}</LotId>
              </LotInfoContainer>
            </InfoSection>

            {/* 금액 섹션 */}
            <div>
              <FormTitle>{texts.amountSection}</FormTitle>
              <AmountSection>
                <FeeCalculationContainer>
                  <FeeRow>
                    <span>{texts.winningBid}</span>
                    <span>{texts.currency} {formatNumber(winningBidPrice)}</span>
                  </FeeRow>
                  <FeeRow>
                    <span>{texts.auctionHouseFee} ({feeRates.auctionHouseFeeRate}%)</span>
                    <span>{texts.currency} {formatNumber(fees.auctionHouseFee)}</span>
                  </FeeRow>
                  <FeeRow>
                    <span>{texts.onlineAuctionFee} ({feeRates.onlineAuctionFeeRate}%)</span>
                    <span>{texts.currency} {formatNumber(fees.onlineAuctionFee)}</span>
                  </FeeRow>
                  <FeeRow>
                    <TooltipContainer>
                      <span>{texts.feeVAT} ({feeRates.vatRate}%)</span>
                      <QuestionIcon 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowVATTooltip(!showVATTooltip);
                        }}
                      >
                        ?
                      </QuestionIcon>
                      <Tooltip visible={showVATTooltip}>
                        {texts.feeVATTooltip}
                      </Tooltip>
                    </TooltipContainer>
                    <span>{texts.currency} {formatNumber(fees.feeVAT)}</span>
                  </FeeRow>
                  <FeeDivider />
                  <TotalRow>
                    <span>{texts.totalAmount}</span>
                    <span>{texts.currency} {formatNumber(fees.totalAmount)}</span>
                  </TotalRow>
                </FeeCalculationContainer>
              </AmountSection>
            </div>

            {/* 결제 시 유의사항 */}
            <PaymentNoticeContainer>
              <PaymentNoticeTitle>
                <InfoIcon viewBox="0 0 24 24">
                  <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2ZM13,17H11V11H13V17ZM13,9H11V7H13V9Z" />
                </InfoIcon>
                {texts.paymentNoticeTitle}
              </PaymentNoticeTitle>
              <PaymentNoticeList>
                <PaymentNoticeItem>{texts.paymentNoticeItem1}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem2}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem3}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem4}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem5}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem6}</PaymentNoticeItem>
                <PaymentNoticeItem>{texts.paymentNoticeItem7}</PaymentNoticeItem>
              </PaymentNoticeList>
            </PaymentNoticeContainer>

            {/* 결제 수단 섹션 */}
            <div>
              <FormTitle>{texts.paymentMethodSection}</FormTitle>
              <PaymentMethodSection>
                <RadioContainer>
                  <RadioOption 
                    selected={selectedPaymentMethod === 'bidspace-pay'}
                    onClick={() => handlePaymentMethodSelect('bidspace-pay')}
                  >
                    <RadioCircle selected={selectedPaymentMethod === 'bidspace-pay'} />
                    <RadioLabel>{texts.bidspacePay}</RadioLabel>
                  </RadioOption>
                  <RadioOption 
                    selected={selectedPaymentMethod === 'bank-transfer'}
                    onClick={() => handlePaymentMethodSelect('bank-transfer')}
                  >
                    <RadioCircle selected={selectedPaymentMethod === 'bank-transfer'} />
                    <RadioLabel>{texts.bankTransfer}</RadioLabel>
                  </RadioOption>
                </RadioContainer>
              </PaymentMethodSection>
            </div>

            {/* 동의 섹션 */}
            <div>
              <AgreementSectionWrapper>
                <AgreementButton
                  isClicked={isIndividualAgreed}
                  onClicked={() => setIsIndividualAgreed(!isIndividualAgreed)}
                >
                  {texts.paymentTermsTitle}
                </AgreementButton>
              </AgreementSectionWrapper>
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <FixedButtonContainer>
            <ButtonWrapper>
              <PrimaryButton 
                onClick={isFormValid() ? handlePaymentSubmit : handleDisabled}
                disabled={!isFormValid()}
              >
                {texts.currency} {formatNumber(fees.totalAmount)} {lang === 'ko' ? '결제하기' : 'Pay Now'}
              </PrimaryButton>
            </ButtonWrapper>
          </FixedButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={Layout}
      desktopView={Layout}
    />
  );
}

export default Payment;
